import {
  type FC,
  memo, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import type { CollectedCardEdition, CollectedCollection } from '@starly/starly-types';

import { desktopBreakpoint } from 'breakpoints';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { useWindowSize } from 'hooks/useWindowSize';
import { selectAuthUserId } from 'store/auth/authSelectors';
import { collectionRequest } from 'store/collection/collectionActions';
import { selectAllCollections } from 'store/collection/collectionSelectors';
import { defaultRarityFilter } from 'store/marketplace/marketplaceReducer';
import type { FilterOption, FormFilters, Option } from 'store/marketplace/marketplaceTypes';
import { userRequest } from 'store/user/userActions';
import type { IExtendedUser } from 'store/user/userReducer';
import { selectAllUsers } from 'store/user/userSelectors';
import { getTags } from 'util/collections';
import CardCollection from 'views/Sale/components/CardCollection/CardCollection';
import type { CollectedCard as CollectedCardType } from 'views/Sale/components/CardCollection/types';
import CollectedCard from 'views/Sale/components/CollectedCard/CollectedCard';
import { StyleCollectionsHeadline } from 'views/Sale/components/OtherCollections/styled';

import type { CardProps } from '../../../components/Card';
import Card from '../../../components/Card';
import CollectedBy from '../../../components/CollectedBy';
import Filters from '../../../components/Filters';
import TrimmedCard from '../../../components/TrimmedCard';
import { sortByFields } from '../../../helpers/sortHelpers';
import { chunkArray } from '../../../util/chunkArray';
import { checkIsDefaultFilters } from '../../Marketplace/helpers';
import { extendedSortTypes } from '../../Marketplace/Marketplace';
import {
  BottomLoader,
  CardList,
  CardsContainer,
  CollectedList,
  FilterContainer,
  NoItemsBlock,
  NoItemsResetText,
  StyleCollectionsHeadlineWrapper,
} from '../styled';
import type { CollectedBlockCard, CollectedBlockCollection } from './types';

const cardStatusOptions: FilterOption[] = [
  {
    value: 'for_sale',
    active: false,
    label: 'Show ‘For Sale’ only',
    deactivate: 'status.not_for_sale',
  },
];

const ownCardStatusOptions: FilterOption[] = [
  {
    value: 'for_sale',
    active: false,
    label: 'For sale',
    deactivate: 'status.not_for_sale',
  },
  {
    value: 'not_for_sale',
    active: false,
    label: 'Not for sale',
    deactivate: 'status.for_sale',
  },
  {
    value: 'staked',
    active: false,
    label: 'Staked',
    deactivate: 'status.not_staked',
  },
  {
    value: 'not_staked',
    active: false,
    label: 'Not staked',
    deactivate: 'status.staked',
  },
];

const propCollectedCardMap: { [key: string]: string } = {
  'create_time desc': '',
  'score_price_ratio desc': 'score_price_ratio desc',
  'price asc edition_id asc': 'price asc edition asc',
  'price desc edition_id asc': 'price desc edition asc',
  'edition_id asc price asc': 'edition asc price asc',
  'edition_id desc price asc': 'edition desc price asc',
  'collector_score desc price asc': 'collector_score desc price asc',
};

const collectedBySortValues = extendedSortTypes.map(({ label, value }) => ({
  label,
  value: propCollectedCardMap[value],
}));

const getCollectionTextForCards = (collection: CollectedCollection) => Object
  .values(collection.collected_cards)
  .map(() => ({
    listing: true,
  }));

const CollectedBlock: FC<{ user: IExtendedUser }> = ({ user }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const isOwnProfile = user.id === useSelector(selectAuthUserId);
  const allCollections = useSelector(selectAllCollections);
  const allUsers = useSelector(selectAllUsers);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [filterCollectionId, setFilterCollectionId] = useSessionStorage(`profile/${user.id}filterCollectionId`, '', history.action === 'POP');
  const [filters, setFilters] = useSessionStorage<FormFilters>(`profile/${user.id}filters`, {
    status: isOwnProfile ? ownCardStatusOptions : cardStatusOptions,
    rarity: defaultRarityFilter,
  }, history.action === 'POP');
  const [sortBy, setSortBy] = useSessionStorage(`profile/${user.id}sortBy`, '', history.action === 'POP');
  const [shownCollectionCount, setShownCollectionCount] = useSessionStorage(`profile/${user.id}CollectionCount`, 1, history.action === 'POP');
  const [showCardsCount, setShowCardsCount] = useSessionStorage(`profile/${user.id}CardsCount`, 1, history.action === 'POP');
  const cardPerLoadCount = 6;

  const collectedCollections: CollectedBlockCollection[] = useMemo(() => {
    const allowedRarities = filters.rarity
      .filter(({ active }) => active)
      .map(({ value }) => value);

    const cardStatuses = filters.status
      ?.filter(({ active }) => active)
      ?.map(({ value }) => {
        const active = !value.startsWith('not');
        const fieldName = `${value}`.replace('not_', '');

        return {
          value: fieldName,
          active,
        };
      });
    return user.collectedCollections
      .reduce((filtered, collection) => {
        const { collected_cards: collectedCards, collection_id: collectionId } = collection;

        if (filterCollectionId && collectionId !== filterCollectionId) {
          return filtered;
        }

        const newCollectedCards: { [key: string]: CollectedCardEdition; } = {};

        Object.keys(collectedCards).forEach((cardKey) => {
          const collectedCard = collectedCards[cardKey];
          if (!allowedRarities.includes(collectedCard.rarity)) { return; }
          const allowed = cardStatuses?.some(
            ({ value, active }) => (!!collectedCard[value as keyof CollectedCardEdition] === active),
          );
          if (allowed || cardStatuses?.length === 0) {
            newCollectedCards[cardKey] = collectedCard;
          }
        });

        if (Object.keys(newCollectedCards).length) {
          filtered.push({
            ...collection,
            collected_cards: newCollectedCards,
            allCards: collectedCards,
          });
        }

        return filtered;
      }, [] as CollectedBlockCollection[]);
  },
  [filterCollectionId, filters, user.collectedCollections]);

  const collectedCollectionsMap: { [key: string]: CollectedCollection } = Object.fromEntries(
    collectedCollections.map((c) => [c.collection_id, c]),
  );

  const collectedCards = useMemo(() => {
    if (!sortBy) return [];
    const order = chunkArray(sortBy.split(' '), 2) as [string, 'asc' | 'desc'][];
    const result = sortByFields<CollectedBlockCard>(
      collectedCollections
        .map(({ collected_cards, collection_id }) => Object
          .values(collected_cards)
          .map((x) => ({
            ...x,
            collection_id: collection_id || '',
            edition: String(x.edition),
            score_price_ratio: (x?.collector_score || 1) / (x.price || 2),
          })))
        .flat(), order,
    );
    return result;
  },
  [sortBy, collectedCollections]);

  const userCollectedCollectionsIds = collectedCollections.map((x) => x.collection_id!);
  const cachedCollections = Object.fromEntries(Object.entries(allCollections)
    .filter((e) => userCollectedCollectionsIds.includes(e[0])));

  /**
    * Render small collection during scroll
    */
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (sortBy) {
            if (showCardsCount < collectedCards.length) {
              setShowCardsCount((v) => v + cardPerLoadCount);
            }
            return;
          }

          if (shownCollectionCount < userCollectedCollectionsIds.length) {
            setShownCollectionCount((v) => v + 1);
          }
        }
      });
    });

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) {
        observer.unobserve(bottomRef.current);
      }
    };
  }, [
    bottomRef,
    sortBy,
    shownCollectionCount,
    filterCollectionId,
    showCardsCount,
    filters,
  ]);

  const getCollectionCards = (collectionData: CollectedCollection) => (
    Object.entries(collectionData?.collected_cards)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, value]) => {
        const cardIdEditionId = key.split('/');
        const cardId = cardIdEditionId[0];
        const cardCollectionId = collectionData.collection_id;
        const cachedCollection = allCollections[cardCollectionId!];
        const cachedItemInfo = cachedCollection?._cached_cards![+cardId - 1] ?? {};
        const item = value;

        const x: CollectedCardType = {
          ...cachedItemInfo,
          title: item.title || cachedItemInfo.title || '',
          media: item.media || cachedItemInfo.media,
          rarity: item.rarity,
          qrCodeUrl: item.qrcode,
          author: collectionData.creator.name,
          collection: collectionData.title,
          totalEditions: cachedItemInfo.editions,
          edition: item.edition,
          forSale: item.for_sale,
          price: item.price,
          state: item.state,
          order: item.order,
          staked: item.staked,
          collector_score: item.collector_score,
          purchase_time: item.purchase_time,
          cardId,
        };
        return x;
      })
  );

  const getSortedCard = (
    card: CollectedBlockCard,
  ): CardProps => {
    const cardId = card.id;
    const cardEdition = card.edition;
    const collectedCollection = collectedCollectionsMap[card.collection_id];
    const cachedCollection = allCollections[card.collection_id];
    const cachedItemInfo = cachedCollection?._cached_cards![+cardId - 1] ?? {};
    const cardProps: CardProps = {
      ...cachedItemInfo,
      edition: cardEdition,
      forSale: card.for_sale,
      price: card.price,
      qrCodeUrl: card.qrcode,
      rarity: card.rarity,
      title: cachedItemInfo.title || card.title || '',
      totalEditions: card.editions,
      author: collectedCollection.creator.name,
      collection: collectedCollection.title,
      cardId,
      staked: card.staked,
    };
    return cardProps;
  };

  useEffect(() => {
    if (sortBy) {
      const collectedCardsCollectionIds = collectedCards
        .map((x) => x.collection_id)
        .filter((x: string, i, arr) => arr.indexOf(x) === i);
      collectedCardsCollectionIds
        .slice(showCardsCount - cardPerLoadCount, showCardsCount).forEach((id) => {
          if (!cachedCollections[id]) {
            dispatch(collectionRequest({ id }));
          }
        });
    }
  }, [showCardsCount, sortBy, filters, filterCollectionId]);

  useEffect(() => {
    // Load first 5 collections to prevent fetching when collection achieved
    if (shownCollectionCount === 1) {
      const collectionsToFetch = userCollectedCollectionsIds.slice(0, 5);
      collectionsToFetch.forEach((collectionId) => {
        if (!cachedCollections[collectionId]) {
          dispatch(collectionRequest({ id: collectionId }));
          dispatch(userRequest({ id: user.collected_collections![collectionId].creator.id }));
        }
      });
    } else {
      const collectionsToFetch = userCollectedCollectionsIds
        .slice(shownCollectionCount + 3, shownCollectionCount + 4);
      collectionsToFetch.forEach((collectionId) => {
        if (!cachedCollections[collectionId]) {
          dispatch(collectionRequest({ id: collectionId }));
          dispatch(userRequest({ id: user.collected_collections![collectionId].creator.id }));
        }
      });
    }
  }, [shownCollectionCount, filters, filterCollectionId, sortBy]);

  const width = useWindowSize();
  const isMobile = width < desktopBreakpoint;
  const collectedCollectionsOpts: Option[] = [
    {
      label: 'All collections',
      value: '',
    },
    ...user.collectedCollections.map(({
      title, collection_id,
    }) => ({ label: title, value: collection_id || '' })),
  ];
  return (
    <>
      {Boolean(user?.collectedCollections?.length) && (
        <>
          <StyleCollectionsHeadlineWrapper>
            <StyleCollectionsHeadline
              data-test-id="collected_by_headline"
              align={width < desktopBreakpoint ? 'left' : 'center'}
              font="secondary"
              textColor="primary"
              size="header2"
              bold
              uppercase
              as="h2"
              data-username-attribute={`${user.name}`}
            >
              {t('collection.user.collectedBy', { username: user.name?.toUpperCase() })}
            </StyleCollectionsHeadline>
          </StyleCollectionsHeadlineWrapper>
          <FilterContainer>
            <Filters
              filters={filters}
              isFilterActive={!checkIsDefaultFilters(filters)}
              onFiltersApply={(values) => {
                setFilters(values);
                if (
                  filters.status?.find(({ value, active }) => value === 'for_sale' && active)
                  && (sortBy.startsWith('price') || sortBy.startsWith('score_price'))
                ) {
                  setSortBy(collectedBySortValues[0].value);
                }
                setShowCardsCount(cardPerLoadCount);
                setShownCollectionCount(1);
              }}
              options={collectedCollectionsOpts}
              onChange={(newValue) => {
                if (newValue) {
                  setFilterCollectionId(newValue.value);
                }
              }}
              onSortChange={(v) => {
                if (v) {
                  setSortBy(v.value);
                }
              }}
              showOption={
                collectedCollectionsOpts.find(({ value }) => value === filterCollectionId)
              }
              sortValue={
                collectedBySortValues.find(({ value }) => value === sortBy)
              }
              sortTypes={collectedBySortValues.filter(({ value }) => {
                if (value.startsWith('price') || value.startsWith('score_price')) {
                  const forSaleOpt = filters.status?.find(({ value: v }) => v === 'for_sale');
                  if (forSaleOpt) {
                    return forSaleOpt.active;
                  }
                }
                return true;
              })}
            />
          </FilterContainer>
        </>
      )}
      {!sortBy && collectedCollections.slice(0, shownCollectionCount).map((collection) => (
        <CollectedList key={collection.collection_id} data-test-id="collected_by_list">
          <CollectedBy
            background={collection.cover_media}
            avatar={allUsers?.[collection.creator.id]?.avatar ?? collection.creator.avatar}
            name={collection.creator.name}
            title={collection.title}
            username={collection.creator.username}
            colId={collection.collection_id!}
            tags={getTags({ ...collection, collected_cards: collection.allCards })}
          >
            <CardList>
              <CardCollection
                cards={getCollectionCards(collection)}
                textForCards={getCollectionTextForCards(collection)}
                collectionId={collection.collection_id}
                creatorUsername={collection.creator.username}
                ownerUsername={user.username}
                isCollectedCollection
              />
            </CardList>
          </CollectedBy>
        </CollectedList>
      ))}
      {!!sortBy && (
        <CardsContainer>
          {collectedCards.slice(0, showCardsCount).map((
            card,
          ) => (
            <CollectedCard
              key={card.nft_item_id}
              card={getSortedCard(card)}
              Component={isMobile ? TrimmedCard : Card}
              textForCard={{ listing: true }}
              username={collectedCollectionsMap[card.collection_id]?.creator?.username}
              collectionId={card.collection_id}
              isCollectedCollection
            />
          ))}
        </CardsContainer>
      )}
      {
        Boolean(user?.collectedCollections?.length)
        && !collectedCards.length
        && !collectedCollections.length
        && (
          <NoItemsBlock>
            <p>There are no items that match filter criteria</p>
            <NoItemsResetText onClick={() => {
              setFilters({
                status: isOwnProfile ? ownCardStatusOptions : cardStatusOptions,
                rarity: defaultRarityFilter,
              });
              setFilterCollectionId('');
              setSortBy('');
            }}
            >
              Reset filters
            </NoItemsResetText>
          </NoItemsBlock>
        )
      }
      <BottomLoader ref={bottomRef} />
    </>
  );
};

export default memo(CollectedBlock);
